import { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

/**
 * Poort voor alle werkgeversschermen (/bedrijf/*, /kandidaten/*, /vacatures/beheer ...).
 *
 * ProtectedRoute controleert alleen of iemand ingelogd is. Deze wrapper controleert
 * daarbovenop dat het account daadwerkelijk een bedrijf (of admin) is. Zonder deze
 * check kan een kandidaat de werkgeversschermen openen; de data blijft dan wel leeg
 * dankzij de databaseregels, maar de omgeving hoort simpelweg niet zichtbaar te zijn.
 */
export const CompanyRoute = ({ children }: { children: ReactNode }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  const { data: access, isLoading } = useQuery({
    queryKey: ["company-route-access", user?.id],
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const [{ data: company }, { data: adminRole }] = await Promise.all([
        supabase.from("companies").select("id").eq("user_id", user!.id).maybeSingle(),
        supabase
          .from("user_roles")
          .select("role")
          .eq("user_id", user!.id)
          .eq("role", "admin")
          .maybeSingle(),
      ]);
      return { isCompany: !!company, isAdmin: !!adminRole };
    },
  });

  if (loading || (user && isLoading)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary" />
      </div>
    );
  }

  if (!user) {
    const target = `/auth?returnUrl=${encodeURIComponent(
      `${location.pathname}${location.search}${location.hash}`,
    )}`;
    return <Navigate to={target} replace />;
  }

  if (!access?.isCompany && !access?.isAdmin) {
    return <Navigate to="/dashboard/kandidaat" replace />;
  }

  return <>{children}</>;
};
